import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { articles } from "@/mocks/articles";
import { comicApps } from "@/mocks/apps";

export default function ArticleDetail({ article }: { article: Article }) {
  const relatedApps = comicApps.filter((a) => article.relatedAppIds.includes(a.id));
  const relatedArticles = articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-4 md:px-6">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
        <article className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="whitespace-nowrap rounded-md bg-secondary-100 px-2 py-0.5 text-secondary-900">{article.category}</span>
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}阅读</span>
          </div>
          <h1 className="mt-3 font-heading text-2xl leading-snug text-foreground-950 md:text-3xl">{article.title}</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>

          <div className="mt-5 flex items-center gap-3 border-y border-background-200 py-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-user-smile-line" aria-hidden="true"></i>
            </span>
            <div className="text-sm">
              <p className="font-bold text-foreground-950">{article.author}</p>
              <p className="text-xs text-foreground-500">漫选社 · 漫画软件应用编辑</p>
            </div>
          </div>

          <div className="mt-6 overflow-hidden rounded-2xl">
            <img src={article.cover} alt={article.title} title={article.title} className="aspect-[16/8] w-full object-cover" />
          </div>

          <div className="mt-8 space-y-6">
            {article.content.map((sec, idx) => (
              <section key={idx}>
                {sec.heading && <h2 className="text-xl font-bold text-foreground-950">{sec.heading}</h2>}
                <div className="mt-3 space-y-3">
                  {sec.paragraphs.map((p, j) => (
                    <p key={j} className="text-base leading-relaxed text-foreground-800">{p}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {relatedApps.length > 0 && (
            <section className="mt-10 rounded-2xl border border-background-200/80 bg-background-100 p-6">
              <h2 className="text-lg font-bold text-foreground-950">文中提到的漫画软件应用</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {relatedApps.map((a) => (
                  <Link key={a.id} to={`/reviews/${a.id}`} className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50">
                    {a.name}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>

        <aside className="space-y-5 lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-2xl border border-background-200/80 bg-background-100 p-5">
            <h3 className="text-base font-bold text-foreground-950">相关文章</h3>
            <ul className="mt-3 space-y-4">
              {relatedArticles.length > 0 ? (
                relatedArticles.map((a) => (
                  <li key={a.id}>
                    <Link to={`/reviews/${a.id}`} className="group flex gap-3">
                      <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img src={a.cover} alt={a.title} title={a.title} className="h-full w-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <p className="line-clamp-2 text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">{a.title}</p>
                        <p className="mt-1 text-xs text-foreground-500">{a.date}</p>
                      </div>
                    </Link>
                  </li>
                ))
              ) : (
                <li className="text-sm text-foreground-600">暂无同分类文章，去看看其他评测吧。</li>
              )}
            </ul>
          </div>

          <div className="rounded-2xl border border-background-200/80 bg-background-100 p-5">
            <h3 className="text-base font-bold text-foreground-950">完整排行榜</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">还不知道漫画软件应用哪个好？查看完整榜单，快速锁定目标。</p>
            <Link to="/rankings" className="mt-4 inline-flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
              查看排行榜
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}